import { useCallback, useEffect, useRef } from 'react';

import type { WebSocketMessage } from '@famecreator/shared-types/websocket';

import { logger } from '@/shared/lib/logger';
import { useWebSocketContext } from '@/shared/providers/use-websocket-context';

interface UseWebSocketMessagesOptions {
  enabled?: boolean;
  onMessage?: (message: WebSocketMessage) => void;
}

/**
 * Hook for subscribing to WebSocket messages using existing connection
 * This hook doesn't create new WebSocket connection, but reuses existing one from context
 */
export function useWebSocketMessages({
  enabled = true,
  onMessage,
}: UseWebSocketMessagesOptions = {}) {
  const websocketContext = useWebSocketContext();
  const subscriberId = useRef(`subscriber_${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    if (!enabled || !onMessage) return;

    // Subscribe to messages
    const currentSubscriberId = subscriberId.current;
    websocketContext.subscribeToMessages(currentSubscriberId, onMessage);

    // Cleanup subscription
    return () => {
      websocketContext.unsubscribeFromMessages(currentSubscriberId);
    };
  }, [enabled, onMessage, websocketContext]);

  return {
    isConnected: websocketContext.isConnected,
    connectionState: websocketContext.connectionState,
    processingCount: websocketContext.processingCount,
  };
}

// Hook specifically for Look Like message handling (preserves existing logic)
export function useLookLikeMessages(
  enabled: boolean = true,
  onMessage?: (message: WebSocketMessage) => void,
) {
  const handleLookLikeMessage = useCallback(
    async (message: WebSocketMessage) => {
      try {
        // Parse message - it might be JSON string
        const data = typeof message === 'string' ? JSON.parse(message) : message;

        // Handle Flutter BestCreator format: { lookLikeInference: {...} }
        if (data.lookLikeInference) {
          // Call external message handler if provided
          if (onMessage) {
            const updateMessage = {
              type: 'LOOK_LIKE_INFERENCE_UPDATE' as const,
              data: data.lookLikeInference,
            };
            onMessage(updateMessage);
          }
          return;
        }

        // Handle legacy format if needed
        if (data.type === 'LOOK_LIKE_STATUS_UPDATE') {
          if (onMessage) {
            onMessage({ type: 'LOOK_LIKE_STATUS_UPDATE', data: data.data });
          }
        }

        // Pass through any other messages
        if (onMessage) {
          onMessage(data);
        }
      } catch (error) {
        logger.websocket.error('Error parsing WebSocket message', { error, message });
      }
    },
    [onMessage],
  );

  // Use the base hook with Look Like specific handling
  return useWebSocketMessages({
    enabled,
    onMessage: handleLookLikeMessage,
  });
}

// Hook specifically for Kontext message handling (preserves existing logic)
export function useKontextMessages(
  enabled: boolean = true,
  onMessage?: (message: WebSocketMessage) => void,
) {
  const handleKontextMessage = useCallback(
    async (message: WebSocketMessage) => {
      try {
        const data = typeof message === 'string' ? JSON.parse(message) : message;

        // Only handle Kontext messages
        if (data.kontext) {
          // Call external message handler if provided
          if (onMessage) {
            onMessage(data);
          }
        }
      } catch (error) {
        logger.websocket.error('Error parsing Kontext WebSocket message', { error, message });
      }
    },
    [onMessage],
  );

  // Use the base hook with Kontext specific handling
  return useWebSocketMessages({
    enabled,
    onMessage: handleKontextMessage,
  });
}

// Hook specifically for Persona Generation message handling
export function usePersonaGenerationMessages(
  enabled: boolean = true,
  onMessage?: (message: WebSocketMessage) => void,
) {
  const handlePersonaGenerationMessage = useCallback(
    async (message: WebSocketMessage) => {
      try {
        const data = typeof message === 'string' ? JSON.parse(message) : message;

        // Handle inference messages (new flow)
        if (data.inference) {
          if (onMessage) {
            onMessage(data);
          }
          return;
        }

        // Handle mediaPersonaGeneration messages (old flow from BestCreator backend)
        if (data.mediaPersonaGeneration) {
          if (onMessage) {
            onMessage(data);
          }
          return;
        }
      } catch (error) {
        logger.websocket.error('Error parsing Persona Generation WebSocket message', {
          error,
          message,
        });
      }
    },
    [onMessage],
  );

  // Use the base hook with Persona Generation specific handling
  return useWebSocketMessages({
    enabled,
    onMessage: handlePersonaGenerationMessage,
  });
}

// Hook specifically for OmniHuman message handling
export function useOmniHumanMessages(
  enabled: boolean = true,
  onMessage?: (message: WebSocketMessage) => void,
) {
  const handleOmniHumanMessage = useCallback(
    async (message: WebSocketMessage) => {
      try {
        const data = typeof message === 'string' ? JSON.parse(message) : message;

        // Handle OmniHuman messages
        if (data.omnihuman) {
          if (onMessage) {
            onMessage(data);
          }
          return;
        }

        // Also handle generic inference messages that might be OmniHuman
        if (data.inference && data.inference.provider === 'omnihuman') {
          if (onMessage) {
            onMessage({
              type: 'OMNIHUMAN_INFERENCE_UPDATE',
              omnihuman: data.inference,
              data: data.inference,
            });
          }
          return;
        }
      } catch (error) {
        logger.websocket.error('Error parsing OmniHuman WebSocket message', { error, message });
      }
    },
    [onMessage],
  );

  // Use the base hook with OmniHuman specific handling
  return useWebSocketMessages({
    enabled,
    onMessage: handleOmniHumanMessage,
  });
}
